import React, { useEffect, useState } from 'react';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { WifiOff, CheckCircle, AlertTriangle } from 'lucide-react';

/**
 * Component that shows network status and allows offline queue management
 */
export const NetworkStatus = ({ position = 'bottom-left', showDetails = false }) => {
  const { isOnline, wasOffline, lastOnline } = useOnlineStatus();
  const [showStatus, setShowStatus] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  // Check for pending items
  useEffect(() => {
    const checkPending = () => {
      try {
        const queue = localStorage.getItem('offline-queue');
        if (queue) {
          const parsed = JSON.parse(queue);
          setPendingCount(parsed.length);
        }
      } catch (error) {
        console.error('Error checking pending count:', error);
      }
    };

    checkPending();
    const interval = setInterval(checkPending, 2000);
    return () => clearInterval(interval);
  }, []);

  // Don't show anything if online and no pending items
  if (isOnline && pendingCount === 0 && !showStatus) {
    return null;
  }

  const positionClasses = {
    'bottom-left': 'bottom-4 left-4',
    'bottom-right': 'bottom-4 right-4',
    'top-left': 'top-4 left-4',
    'top-right': 'top-4 right-4'
  };

  const getStatusColor = () => {
    if (!isOnline) return 'bg-red-500';
    if (wasOffline && pendingCount > 0) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getStatusIcon = () => {
    if (!isOnline) return <WifiOff size={20} />;
    if (wasOffline && pendingCount > 0) return <AlertTriangle size={20} />;
    return <CheckCircle size={20} />;
  };

  return (
    <div className={`fixed ${positionClasses[position]} z-50`}>
      {/* Status indicator */}
      <div
        className={`
          ${getStatusColor()} text-white px-4 py-3 rounded-lg shadow-lg
          cursor-pointer transition-all duration-300 hover:scale-105
          flex items-center gap-3 min-w-[250px]
        `}
        onClick={() => setShowStatus(!showStatus)}
      >
        {getStatusIcon()}
        <div className="flex-1">
          <div className="font-semibold text-sm">
            {!isOnline ? 'Çevrimdışı' : wasOffline ? 'Yeniden Bağlanıyor' : 'Çevrimiçi'}
          </div>
          {showDetails && (
            <div className="text-xs opacity-90">
              {pendingCount > 0 && `${pendingCount} bekleyen işlem`}
            </div>
          )}
        </div>
        {pendingCount > 0 && (
          <div className="bg-white bg-opacity-20 rounded-full px-2 py-1 text-xs font-bold">
            {pendingCount}
          </div>
        )}
      </div>

      {/* Expanded details */}
      {showStatus && (
        <div className="mt-2 bg-white rounded-lg shadow-xl border border-gray-200 p-4 min-w-[300px]">
          <h3 className="font-bold text-gray-800 mb-2">Ağ Durumu</h3>

          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${isOnline ? 'bg-green-500' : 'bg-red-500'}`} />
              <span className="text-gray-700">
                Durum: <span className="font-semibold">{isOnline ? 'Çevrimiçi' : 'Çevrimdışı'}</span>
              </span>
            </div>

            {lastOnline && (
              <div className="flex items-center gap-2">
                <CheckCircle size={14} className="text-gray-400" />
                <span className="text-gray-700">
                  Son bağlantı: <span className="font-semibold">{new Date(lastOnline).toLocaleTimeString('tr-TR')}</span>
                </span>
              </div>
            )}

            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${pendingCount > 0 ? 'bg-yellow-500' : 'bg-green-500'}`} />
              <span className="text-gray-700">
                Bekleyen işlemler: <span className="font-semibold">{pendingCount}</span>
              </span>
            </div>
          </div>

          <div className="mt-3 pt-3 border-t border-gray-200">
            <div className="text-xs text-gray-500 space-y-1">
              {!isOnline && (
                <p>• Değişiklikler yerel olarak kaydedilecek</p>
              )}
              {isOnline && pendingCount > 0 && (
                <p>• İşlemler otomatik olarak senkronize edilecek</p>
              )}
              <p>• Bağlantı yeniden kurulduğunda bildirim alacaksınız</p>
            </div>
          </div>

          <button
            onClick={() => setShowStatus(false)}
            className="mt-3 w-full bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm py-2 rounded-lg transition-colors"
          >
            Kapat
          </button>
        </div>
      )}
    </div>
  );
};

export default NetworkStatus;
